// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import XngZone from "@ces/App/Components/Common/XngZone";
import { constants, DivWrapper } from "@ces/XDL/constants";
import { color, layoutContainer } from "@ces/XDL/Utilities";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

const StyledWrapperX = getStyledWrapper("div") as any; // typeof StyledWrapper & { sticky: boolean };

interface Props extends WidgetProps {
    fields: {
        rowCount: number;
        gap: number;
        // XNG-Changes: add margin and overrideStyle and itemsList
        margin: string;
        overrideStyle: boolean;
        itemsList: {
            fields: {
                name: string;
                fullWidth: boolean;
                removeFooterOffset: boolean;
                addSeparate: boolean;
            };
        }[];
    };
}

// XNG-Changes: add styles
const styles: { container?: InjectableCss; itemContainer?: InjectableCss } = {
    container: {
        css: css`
            max-width: 100%;
            ${({ margin }: any) => margin && `margin: ${margin};`}
            > div {
                margin-bottom: ${({ gap }: any) => gap}px;
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
    itemContainer: {
        css: css`
            width: 100%;
            ${layoutContainer}

            ${({ removeFooterOffset, addSeparate }: any) => css`
                ${removeFooterOffset &&
                css`
                    margin-bottom: -${constants.footer.offsetTop}px;
                `}
                ${addSeparate &&
                css`
                    & > ${DivWrapper} > div:nth-of-type(1):not(:empty) {
                        border-top: 1px solid ${color("common.border")};
                        padding-top: 16px;
                    }
                    & > ${DivWrapper} > div:empty {
                        display: none;
                    }
                `}
            `}
        `,
    },
};

// A widget that makes a configurable number of row zones within its container.
const Rows: React.FunctionComponent<Props> = ({
    fields: { margin, rowCount, gap, overrideStyle, itemsList },
    id,
}: Props) => {
    const rows: JSX.Element[] = [];

    // XNG-Changes: handle overrideStyle case
    if (overrideStyle) {
        itemsList?.forEach((item, index) => {
            const { fullWidth: isFullWidthItem, removeFooterOffset, addSeparate, name: itemName = "" } = item.fields;
            const name = itemName.replace(/\s/g, "");
            // "full-width" class for "LayoutContainer" style
            const className = `${isFullWidthItem ? "full-width" : ""}`;

            const itemProps = {
                addSeparate,
                removeFooterOffset: removeFooterOffset && index === itemsList.length - 1,
            };

            rows.push(
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper
                    key={`StyledWrapper-${id}-${name || index}`}
                    className={className}
                    {...itemProps}
                    {...styles.itemContainer}
                >
                    <XngZone contentId={id} zoneName={`Content_${name || index}`} hint={`Zone for ${itemName}`} />
                </StyledWrapper>,
            );
        });
    } else {
        for (let row = 0; row < rowCount; row = row + 1) {
            rows.push(
                <GridItem key={`GridItem-${id}-${row}`} width={[12, 12, 12, 12, 12]}>
                    <Zone contentId={id} zoneName={`Content${row}`} />
                </GridItem>,
            );
        }
    }

    // XNG-Changes: handle overrideStyle case
    if (overrideStyle) {
        return (
            <StyledWrapperX gap={gap} margin={margin} {...styles.container}>
                {rows}
            </StyledWrapperX>
        );
    }

    return (
        <GridContainer
            gap={gap}
            // XNG-Changes: add margin
            css={css`
                ${margin &&
                css`
                    margin: ${margin};
                `}
            `}
        >
            {rows}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: Rows,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: "rowCount",
                displayName: "Row Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                isVisible: item => !item.fields.overrideStyle,
            },
            {
                name: "gap",
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
            },
            // XNG-Changes: add margin field
            {
                name: "margin",
                displayName: "Margin Size",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0",
                placeholder: "Enter: 5px, 5px auto, ...etc",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("margin", value) ? null : `Invalid Input`;
                },
            },
            // XNG-Changes: add overrideStyle field
            {
                name: "overrideStyle",
                displayName: "Override Style",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            // XNG-Changes: add itemsList field
            {
                name: "itemsList",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name || "Zone";
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => item.fields.overrideStyle,
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Zone Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        placeholder: "Zone Name",
                        isRequired: true,
                    },
                    {
                        name: "fullWidth",
                        displayName: "Full Width",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "addSeparate",
                        displayName: "Add Separate",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "removeFooterOffset",
                        displayName: "Remove Footer Offset",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        tooltip: "Use it to remove footer offset, It'll apply for the last item only",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
